// 🧠 PLUTUS AI INTELLIGENT AGENT WITH VOICE
import { analyzeWithOpenAI } from './openai.js';

class ProAIAgent {
  constructor() {
    this.personality = {
      name: 'Plutus AI',
      title: 'Senior Institutional Trading Analyst',
      experience: '15+ years at Goldman Sachs & JP Morgan',
      specialty: ['Gold', 'Forex', 'Crypto', 'Indices', 'Commodities'],
      characteristics: {
        tone: 'professional_conversational',
        expertise_level: 'institutional',
        communication_style: 'natural_expert',
        response_style: 'chatgpt_intelligent_fast'
      }
    };
    
    this.conversationMemory = [];
    this.systemPrompt = this.buildSystemPrompt();
    
    this.voiceSettings = {
      rate: 0.9,
      pitch: 1.0,
      volume: 0.9,
      lang: 'en-US'
    };
    
    this.init();
  }

  init() {
    console.log('🧠 Plutus AI Intelligent Agent Ready');
  }

  buildSystemPrompt() {
    return `You are Plutus AI, a Senior Institutional Trading Analyst with over 15 years of experience at Goldman Sachs and JP Morgan. You specialize in Gold (XAUUSD) trading and provide institutional-grade analysis.

🎯 **PERSONALITY & COMMUNICATION:**
- Be conversational, natural, and helpful like ChatGPT
- Respond quickly and concisely 
- Maintain professional expertise while being approachable
- Always provide actionable insights, not just theory
- Use emojis sparingly but effectively for readability
- Keep responses focused and to-the-point
- Be encouraging and supportive to traders

🏆 **EXPERTISE AREAS:**
- Gold/XAUUSD market structure and price action
- Technical analysis and chart patterns  
- Risk management and position sizing
- Market psychology and institutional flow
- Support/resistance levels and key zones
- Trade setup development with R:R ratios
- Real-time market commentary

📊 **RESPONSE STRUCTURE:**
When providing trade setups, use this format:
- **Direction:** LONG or SHORT
- **Entry:** [specific price]
- **Stop Loss:** [specific price]
- **TP1:** [specific price] 
- **TP2:** [specific price]
- **R:R Ratio:** [calculated ratio]

🎯 **CONVERSATION STYLE:**
- Answer questions naturally and quickly
- Build on previous conversation context when relevant
- Ask follow-up questions when appropriate
- Provide examples and analogies when helpful
- Be patient with beginner questions
- Show genuine interest in helping traders succeed
- Prioritize speed and clarity over lengthy explanations

🔥 **CURRENT MARKET FOCUS:**
- Always reference current Gold price levels
- Relate analysis to live market conditions
- Consider global macro factors (USD, rates, inflation)
- Factor in trading session timing (Asian/London/NY)
- Acknowledge market volatility and uncertainty

⚠️ **RISK DISCLAIMER:**
Always remind users that:
- This is educational content, not financial advice
- Trading involves significant risk of loss
- Proper risk management is essential
- Never risk more than you can afford to lose

Respond naturally and intelligently to any trading-related question, maintaining your professional expertise while being conversational and helpful.`;
  }

  // ✅ CORRECTION - AJOUT DE LA MÉTHODE generateVoiceResponse
  async generateVoiceResponse(text, context = {}) {
    try {
      console.log('🎤 generateVoiceResponse called:', {
        hasText: !!text,
        textLength: text?.length || 0,
        context,
        timestamp: new Date().toISOString()
      });

      if (!text || typeof text !== 'string') {
        console.log('❌ Invalid text for voice response');
        return;
      }

      // Check if speech synthesis is available
      if (!window.speechSynthesis) {
        console.log('❌ Speech synthesis not available');
        return;
      }

      // Stop any ongoing speech
      window.speechSynthesis.cancel();

      // Prepare voice-friendly text
      const voiceText = await this.prepareVoiceResponse(text, context);
      
      console.log('🎤 Prepared voice text:', voiceText.substring(0, 100) + '...');

      // Create speech utterance
      const utterance = new SpeechSynthesisUtterance(voiceText);
      
      // Configure voice settings
      utterance.rate = this.voiceSettings.rate;
      utterance.pitch = this.voiceSettings.pitch;
      utterance.volume = this.voiceSettings.volume;
      utterance.lang = this.voiceSettings.lang;

      // Try to select a good voice
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.lang.startsWith('en') && 
        (voice.name.includes('Microsoft') || voice.name.includes('Google') || voice.name.includes('Alex'))
      ) || voices.find(voice => voice.lang.startsWith('en'));

      if (preferredVoice) {
        utterance.voice = preferredVoice;
        console.log('🎤 Using voice:', preferredVoice.name);
      }

      // Event handlers
      utterance.onstart = () => {
        console.log('🎤 Voice synthesis started');
      };

      utterance.onend = () => {
        console.log('🎤 Voice synthesis completed');
      };

      utterance.onerror = (error) => {
        console.error('❌ Voice synthesis error:', error);
      };

      // Speak the text
      window.speechSynthesis.speak(utterance);

    } catch (error) {
      console.error('❌ generateVoiceResponse error:', error);
    }
  }

  // 🧠 MAIN INTELLIGENT CHAT RESPONSE
  async generateChatResponse(userMessage, context = {}) {
    try {
      const currentPrice = context.currentPrice || 4150;
      const market = context.market || 'Gold';
      const isVoiceQuery = context.isVoiceQuery || false;
      const hasContext = context.hasContext || false;

      console.log('🧠 Generating chat response...', {
        userMessage: userMessage.substring(0, 50),
        currentPrice,
        market,
        isVoiceQuery
      });

      // Add user message to memory
      this.addToMemory('user', userMessage);

      // Build conversation context from memory
      const conversationContext = this.buildConversationContext();

      // Create intelligent prompt for OpenAI
      const intelligentPrompt = this.buildIntelligentPrompt(
        userMessage, 
        currentPrice, 
        market, 
        conversationContext,
        isVoiceQuery
      );

      console.log('📝 Sending to OpenAI:', {
        promptLength: intelligentPrompt.length,
        hasMemory: this.conversationMemory.length > 0,
        isVoiceQuery
      });

      // ✅ USE analyzeWithOpenAI DIRECTLY - Get model from localStorage
      const savedModel = localStorage.getItem('snapchart_openai_model') || localStorage.getItem('atlas_openai_model') || 'gpt-5.5';
      const result = await analyzeWithOpenAI(intelligentPrompt, savedModel);
      
      if (!result || !result.choices?.[0]?.message?.content) {
        throw new Error('No response received from OpenAI');
      }

      let response = result.choices[0].message.content.trim();

      // Post-process response for quality
      response = this.postProcessResponse(response, market, currentPrice);

      // Add Plutus AI response to memory
      this.addToMemory('snapchart', response);

      console.log('✅ Chat response generated:', {
        responseLength: response.length,
        hasTradeSetup: response.includes('Entry:') || response.includes('LONG') || response.includes('SHORT'),
        isVoiceOptimized: isVoiceQuery,
        modelUsed: result.model_used
      });

      return response;

    } catch (error) {
      console.error('❌ Chat response error:', error);
      return this.getIntelligentFallbackResponse(userMessage, context, error);
    }
  }

  // 📝 BUILD INTELLIGENT PROMPT FOR OPENAI
  buildIntelligentPrompt(userMessage, currentPrice, market, conversationContext, isVoiceQuery) {
    let prompt = `${this.systemPrompt}\n\n`;

    // Add current market context
    prompt += `**CURRENT MARKET CONTEXT:**\n`;
    prompt += `- Market: ${market}\n`;
    prompt += `- Current Price: $${currentPrice.toLocaleString()}\n`;
    prompt += `- Timestamp: ${new Date().toLocaleString()}\n`;
    
    if (isVoiceQuery) {
      prompt += `- Input Method: Voice (user spoke this question)\n`;
      prompt += `- Response Style: Optimize for voice output (conversational, not too long)\n`;
    }
    
    prompt += `\n`;

    // Add conversation history if available (limited for speed)
    if (conversationContext.length > 0) {
      prompt += `**RECENT CONVERSATION:**\n`;
      conversationContext.slice(-2).forEach((msg, index) => {
        prompt += `${index + 1}. ${msg.sender}: ${msg.message}\n`;
      });
      prompt += `\n`;
    }

    // Add user's current question
    prompt += `**USER'S QUESTION:**\n`;
    prompt += `"${userMessage}"\n\n`;

    // Add specific instructions
    prompt += `**RESPONSE INSTRUCTIONS:**\n`;
    prompt += `- Answer naturally and quickly like ChatGPT\n`;
    prompt += `- Use your expertise as Plutus AI, the institutional trading analyst\n`;
    prompt += `- Reference the current Gold price of $${currentPrice.toLocaleString()} when relevant\n`;
    prompt += `- If providing trade analysis, include specific levels and risk management\n`;
    prompt += `- Keep the tone professional but approachable\n`;
    prompt += `- Be concise and to-the-point - prioritize speed and clarity\n`;
    prompt += `- Use markdown formatting for better readability\n`;
    
    if (isVoiceQuery) {
      prompt += `- Optimize for voice: be conversational, not too technical, avoid excessive formatting\n`;
    }
    
    prompt += `\nProvide your response as Plutus AI:`;

    return prompt;
  }

  // 🧠 BUILD CONVERSATION CONTEXT FROM MEMORY
  buildConversationContext() {
    return this.conversationMemory.slice(-2).map(msg => ({
      sender: msg.sender === 'user' ? 'User' : 'Plutus AI',
      message: msg.message.substring(0, 100)
    }));
  }

  // ✨ POST-PROCESS RESPONSE FOR QUALITY
  postProcessResponse(response, market, currentPrice) {
    // Ensure market name consistency
    response = response.replace(/XAUUSD/g, market);
    response = response.replace(/Gold\/USD/g, market);
    
    // Ensure current price is referenced accurately
    const priceRegex = /\$[\d,]+/g;
    const prices = response.match(priceRegex);
    if (prices) {
      prices.forEach(price => {
        const numPrice = parseFloat(price.replace(/[\$,]/g, ''));
        if (Math.abs(numPrice - currentPrice) > 500 && numPrice > 3000 && numPrice < 5000) {
          response = response.replace(price, `$${currentPrice.toLocaleString()}`);
        }
      });
    }

    // Ensure proper formatting (faster)
    if (!response.includes('**') && response.length > 200) {
      const lines = response.split('\n');
      if (lines.length > 3) {
        response = lines.map((line, index) => {
          if (index === 0 && !line.includes('**') && line.length > 20) {
            return `**${line}**`;
          }
          return line;
        }).join('\n');
      }
    }

    return response.trim();
  }

  // 🔄 FALLBACK RESPONSE INTELLIGENT
  getIntelligentFallbackResponse(userMessage, context, error) {
    const market = context.market || 'Gold';
    const currentPrice = context.currentPrice || 4150;
    
    console.log('⚠️ Using intelligent fallback response');
    
    const messageType = this.analyzeMessageIntent(userMessage);
    
    switch (messageType) {
      case 'greeting':
        return `**Hello! I'm Plutus AI** 👋\n\n` +
               `Your AI trading assistant ready to help with ${market} analysis and insights.\n\n` +
               `**Current ${market}:** $${currentPrice.toLocaleString()}\n\n` +
               `What would you like to know about the market? I can help with:\n` +
               `• Price analysis and predictions\n` +
               `• Trade setups with specific levels\n` +
               `• Risk management strategies`;
      
      case 'price_question': {
        // Derive a "key zone" dynamically from the current price
        // (~0.5% band) instead of hardcoding $4,150 — that was a
        // gold-era relic that made zero sense for BTC/crypto/other
        // assets and produced nonsense responses like "Watch $4,150"
        // when the user was asking about a $66K BTC chart.
        const keyZone = Math.round(currentPrice * 0.995);
        return `**${market} Current Price: $${currentPrice.toLocaleString()}**\n\n` +
               `${market} is trading around $${currentPrice.toLocaleString()}. Reading the chart structure will tell you whether momentum favors the upside or a reversion.\n\n` +
               `**Quick Market Context:**\n` +
               `• Immediate Zone: ~$${keyZone.toLocaleString()} (0.5% band)\n` +
               `• Volatility: read from recent ATR / candle sizes on your chart\n` +
               `• Trend Read: look for higher-highs/higher-lows sequence on your active TF\n\n` +
               `Ask me anything specific about ${market}!`;
      }

      case 'trade_setup':
        // No fake bias from a hardcoded threshold. If you want a real
        // directional read, upload the chart to /trading — this fallback
        // path just illustrates a symmetric R:R around current price.
        const isLong = currentPrice % 2 === 0; // arbitrary + clearly a placeholder — do NOT trust
        const direction = isLong ? 'LONG' : 'SHORT';
        const entry = currentPrice;
        const sl = isLong ? Math.round(currentPrice * 0.985) : Math.round(currentPrice * 1.015);
        const tp1 = isLong ? Math.round(currentPrice * 1.02) : Math.round(currentPrice * 0.98);
        const tp2 = isLong ? Math.round(currentPrice * 1.035) : Math.round(currentPrice * 0.965);
        const rr = Math.abs(tp1 - entry) / Math.abs(entry - sl);
        
        return `**Quick ${market} Setup Analysis**\n\n` +
               `Based on current levels around $${currentPrice.toLocaleString()}:\n\n` +
               `• **Direction:** ${direction}\n` +
               `• **Entry:** $${entry.toLocaleString()}\n` +
               `• **Stop Loss:** $${sl.toLocaleString()}\n` +
               `• **TP1:** $${tp1.toLocaleString()}\n` +
               `• **TP2:** $${tp2.toLocaleString()}\n` +
               `• **R:R Ratio:** 1:${rr.toFixed(1)}\n\n` +
               `**Risk Management:** Position size 1-2% of capital max.\n\n` +
               `*Always manage risk properly!*`;
      
      default:
        return `I understand you're asking about: **"${userMessage}"**\n\n` +
               `**Current ${market}:** $${currentPrice.toLocaleString()}\n\n` +
               `I'm processing your request.\n\n` +
               `**I can help with:**\n` +
               `• Market analysis and price predictions\n` +
               `• Trade setups with specific entry/exit levels\n` +
               `• Technical analysis and chart interpretation\n` +
               `• Risk management strategies\n\n` +
               `Please try your question again - I'm ready to provide detailed analysis!`;
    }
  }

  // 🧠 Simple Message Intent Analysis
  analyzeMessageIntent(message) {
    const msg = message.toLowerCase();
    
    if (/\b(hello|hi|hey|good morning|good afternoon|good evening|bonjour|salut)\b/.test(msg)) {
      return 'greeting';
    }
    
    if (/\b(price|current|level|where|trading|quote|cost|value)\b/.test(msg)) {
      return 'price_question';
    }
    
    if (/\b(trade|setup|entry|exit|buy|sell|long|short|position|levels)\b/.test(msg)) {
      return 'trade_setup';
    }
    
    if (/\b(analyz|analysis|view|opinion|think|outlook|forecast|predict)\b/.test(msg)) {
      return 'market_analysis';
    }
    
    return 'general_question';
  }

  // 🎤 VOICE RESPONSE PREPARATION
  async prepareVoiceResponse(text, context = {}) {
    const market = context.market || 'Gold';
    const price = context.price || 4150;
    const maxLength = context.maxLength || 250;

    try {
      if (!text || typeof text !== 'string') {
        return `Hi, this is Plutus AI. I'm ready to help with your ${market} trading questions.`;
      }

      console.log('🎤 Preparing voice response:', {
        originalLength: text.length,
        maxLength,
        market
      });

      // Convert markdown to speech-friendly text
      let voiceText = this.convertToSpeechText(text);
      
      // Optimize length for voice
      if (voiceText.length > maxLength) {
        voiceText = this.summarizeForVoice(voiceText, market, price, maxLength);
      }

      // Final cleanup for speech
      voiceText = this.finalVoiceCleanup(voiceText);

      console.log('✅ Voice response prepared:', {
        finalLength: voiceText.length,
        preview: voiceText.substring(0, 100) + '...'
      });

      return voiceText;

    } catch (error) {
      console.error('❌ Voice preparation error:', error);
      return `Hi, this is Plutus AI. I'm here to help with ${market} trading analysis. Let me know what you'd like to know.`;
    }
  }

  // 🔄 Convert markdown text to speech-friendly format
  convertToSpeechText(text) {
    return text
      // Remove markdown formatting
      .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
      .replace(/\*(.*?)\*/g, '$1') // Italic
      .replace(/#{1,6}\s*/g, '') // Headers
      .replace(/•\s*/g, '') // Bullets
      .replace(/- \*\*/g, '') // Dash bullets with bold
      .replace(/\n\n+/g, '. ') // Double newlines to periods
      .replace(/\n/g, ' ') // Single newlines to spaces
      
      // Convert financial terms for speech
      .replace(/USD/g, 'dollars')
      .replace(/XAUUSD/g, 'Gold')
      .replace(/BTCUSD/g, 'Bitcoin')
      .replace(/EURUSD/g, 'Euro Dollar')
      .replace(/([0-9]+)%/g, '$1 percent')
      .replace(/\$([0-9,]+)/g, '$1 dollars')
      .replace(/([0-9]+):([0-9]+)/g, '$1 to $2')
      .replace(/R:R/g, 'Risk to Reward')
      .replace(/TP1/g, 'Take Profit 1')
      .replace(/TP2/g, 'Take Profit 2')
      .replace(/SL/g, 'Stop Loss')
      
      // Clean up spacing and punctuation
      .replace(/\s+/g, ' ')
      .replace(/\.\s*\./g, '.')
      .trim();
  }

  // ✂️ Intelligent summarization for voice
  summarizeForVoice(text, market, price, maxLength) {
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    
    let summary = '';
    let currentLength = 0;
    const maxSentences = Math.min(sentences.length, 3);
    
    // Prioritize sentences with key trading info
    const priorityKeywords = ['entry', 'stop', 'target', 'long', 'short', 'price', 'support', 'resistance'];
    
    // Sort sentences by importance
    const sortedSentences = sentences.sort((a, b) => {
      const aScore = priorityKeywords.reduce((score, keyword) => 
        score + (a.toLowerCase().includes(keyword) ? 1 : 0), 0);
      const bScore = priorityKeywords.reduce((score, keyword) => 
        score + (b.toLowerCase().includes(keyword) ? 1 : 0), 0);
      return bScore - aScore;
    });
    
    // Build summary with most important sentences
    for (let i = 0; i < maxSentences && currentLength < maxLength * 0.8; i++) {
      const sentence = sortedSentences[i].trim();
      if (sentence && currentLength + sentence.length < maxLength) {
        if (summary) summary += '. ';
        summary += sentence;
        currentLength += sentence.length;
      }
    }
    
    if (!summary) {
      summary = `Here's my analysis of ${market} at ${price} dollars. ${sentences[0]?.substring(0, 80) || 'Market conditions are being analyzed'}`;
    }
    
    return summary.trim() + '.';
  }

  // 🎯 Final cleanup for natural speech
  finalVoiceCleanup(text) {
    return text
      .replace(/\s+/g, ' ')
      .replace(/([.!?])\s*([.!?])/g, '$1')
      .replace(/^[.!?\s]+/, '')
      .replace(/[.!?\s]+$/, '')
      .trim();
  }

  // 🧠 Memory Management
  addToMemory(sender, message) {
    this.conversationMemory.push({
      sender,
      message: typeof message === 'string' ? message.substring(0, 200) : 'Non-text message',
      timestamp: Date.now()
    });

    // Keep last 4 messages for better performance
    if (this.conversationMemory.length > 4) {
      this.conversationMemory = this.conversationMemory.slice(-4);
    }
  }

  // 🔄 Legacy method compatibility
  async generateProAnalysis(market, priceData, context = {}) {
    const price = priceData.price || priceData;
    
    const analysisPrompt = `Provide a comprehensive analysis of ${market} at $${price}. ` +
                          `Include current market bias, key levels, potential trade setups, ` +
                          `and risk management recommendations. Be concise and actionable.`;
    
    return this.generateChatResponse(analysisPrompt, {
      currentPrice: price,
      market,
      isVoiceQuery: false,
      hasContext: true
    });
  }

  // ✅ buildAnalysisPrompt
  buildAnalysisPrompt(market, price, context) {
    const hasImages = context.hasImages || false;
    const userQuestion = context.userQuestion || `Analyze ${market} market conditions`;
    const imageCount = context.imageCount || 0;
    
    let prompt = `You are Plutus AI, a Senior Institutional Trading Analyst with over 15 years of experience at Goldman Sachs and JP Morgan.

**CURRENT MARKET CONTEXT:**
- Market: ${market}
- Current Price: $${price.toLocaleString()}
- Timestamp: ${new Date().toLocaleString()}
${hasImages ? `- Chart Images: ${imageCount} uploaded for analysis` : ''}

**USER'S REQUEST:**
"${userQuestion}"

**ANALYSIS INSTRUCTIONS:**
- Provide professional, institutional-grade analysis
- Reference the current ${market} price of $${price.toLocaleString()}
- If trade setups are relevant, include specific entry, stop loss, and target levels
- Use markdown formatting for better readability
- Be concise but comprehensive
- Include risk management recommendations
${hasImages ? '- Analyze the uploaded chart images in detail' : ''}

**RESPONSE STYLE:**
- Professional but conversational tone
- Quick and actionable insights
- Specific price levels and ratios
- Clear market bias and reasoning

Provide your response as Plutus AI:`;

    return prompt;
  }

  // 🎯 Main method for compatibility
  async generateProfessionalChatResponse(userMessage, context = {}) {
    return this.generateChatResponse(userMessage, context);
  }

  // ❌ Error Response
  getErrorResponse(errorMessage) {
    return `I apologize, but I encountered a technical issue while processing your request.\n\n` +
           `**Error:** ${errorMessage}\n\n` +
           `**Quick Solutions:**\n` +
           `• Check your OpenAI API key in settings\n` +
           `• Verify your internet connection\n` +
           `• Try asking your question in a different way\n` +
           `• Refresh the page if the problem persists\n\n` +
           `I'm still here to help with your trading questions once this is resolved!`;
  }
}

// 🚀 Export singleton instance
let proAIAgent;
try {
  proAIAgent = new ProAIAgent();
  console.log('✅ Plutus AI Agent initialized successfully');
} catch (error) {
  console.error('❌ ProAIAgent initialization error:', error);
  // Fallback object
  proAIAgent = {
    generateChatResponse: async (msg, ctx) => {
      return `**I'm Plutus AI, your trading assistant** 🏦\n\n` +
             `Please check your OpenAI API configuration and try again.\n\n` +
             `**Current ${ctx.market || 'Gold'}:** $${ctx.currentPrice?.toLocaleString() || '4,150'}\n\n` +
             `I'll be back with full intelligence shortly!`;
    },
    generateVoiceResponse: async (text, context) => {
      console.log('🎤 Fallback voice response');
      if (window.speechSynthesis) {
        const utterance = new SpeechSynthesisUtterance('Plutus AI here, ready to assist with trading analysis.');
        window.speechSynthesis.speak(utterance);
      }
    },
    prepareVoiceResponse: async (text) => 'Plutus AI here, ready to assist with trading analysis.',
    generateProfessionalChatResponse: async (msg, ctx) => {
      return proAIAgent.generateChatResponse(msg, ctx);
    },
    buildAnalysisPrompt: (market, price, context) => {
      return `Analyze ${market} at $${price} with current market conditions. Provide professional trading insights.`;
    }
  };
}

export default proAIAgent;